import {
	Box,
	Button,
	Flex,
	FlexProps,
	IconButton,
	Image,
	Menu,
	MenuItem,
	MenuList,
	Text,
	Tooltip,
	useMediaQuery,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import React, { useEffect } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import {
	BsCloudUploadFill,
	BsPersonFill,
	BsFolderFill,
	BsGearWideConnected,
	BsLayersHalf,
} from "react-icons/bs";
import { GrHomeRounded } from "react-icons/gr";
import {
	// MdKeyboardDoubleArrowLeft,
	MdKeyboardDoubleArrowRight,
	MdArrowBackIosNew,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
	Link,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { Mixpanel } from "./Mixpanel.js";
import DashnodeLogo from "./img/Dashnode_logo.png";
import FileUploadPageLoader from "./img/loader.gif";
import checkVerifiedBlue from "./img/check-verified-blue.svg";
import ShieldZapGreen from "./img/shield-zap-green.svg";
import {
	AppDispatch,
	ERROR_MSG,
	formatErrorMessage,
	logoutAce,
	PageLoading,
	SET_MESSAGE,
	setCustomerData,
	setPageLoading,
	BasicDialog,
	GlobalUploadProgress,
	refreshSubscriptionPlan,
	MarkdownPreset,
	setLocalePrefs,
	fetchUserDetails,
	MissingInfoPopup,
} from "./packages";
import { FiMail } from "react-icons/fi";
import { IoWallet } from "react-icons/io5";
// const PricingModel = lazy(() => import("./common/PricingModel"));

interface IconTextProps extends FlexProps {
	Icon: typeof GrHomeRounded;
	text: string;
	active?: boolean;
	link?: string;
	disabled?: boolean;
	uniqueIdentifier: string;
}

interface IconMiniProps extends FlexProps {
	Icon: typeof GrHomeRounded;
	active?: boolean;
	link?: string;
	text: string;
	uniqueIdentifier: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}
const IconText = ({
	Icon,
	text,
	link,
	disabled,
	uniqueIdentifier,
	active = false,
	...rest
}: IconTextProps) => {
	// const width = window.innerWidth;

	if (active) {
		return (
			<Flex
				id={uniqueIdentifier}
				className={uniqueIdentifier}
				justifyContent="start"
				alignItems="center"
				{...rest}
				width="100%"
				bg={"rgba(0, 135, 238, 0.1)"}
				borderRadius="md"
				as={Link}
				to={link}
				// onClick={handleClick}
			>
				<Icon size="20px" color="#0087ee" />
				<Box ml="11px">
					<Text
						textStyle="primary.text"
						fontWeight={"500"}
						fontSize="16px"
						color="#0087ee"
					>
						{text}
					</Text>
				</Box>
			</Flex>
		);
	}

	if (disabled) {
		return (
			<Flex
				justifyContent="start"
				alignItems="center"
				{...rest}
				borderRadius={"10px"}
				id={uniqueIdentifier}
				className={uniqueIdentifier}
			>
				<Icon size="20px" />
				<Box ml="11px">
					<Text textStyle="primary.text" fontSize="16px">
						{text}
					</Text>
				</Box>
			</Flex>
		);
	}

	return (
		<Link to={link}>
			<Flex
				justifyContent="start"
				alignItems="center"
				{...rest}
				borderRadius={"10px"}
				width="100%"
				id={uniqueIdentifier}
				className={uniqueIdentifier}
			>
				<Icon size="20px" />
				<Box ml="11px">
					<Text textStyle="primary.text" fontSize="16px">
						{text}
					</Text>
				</Box>
			</Flex>
		</Link>
	);
};

const IconMini = ({
	Icon,
	link,
	text,
	active = false,
	onClick,
	uniqueIdentifier,
	...rest
}: IconMiniProps) => {
	const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (text !== "My Profile") {
			localStorage.removeItem("uploadedFiles");
			localStorage.removeItem("selected_items");
			localStorage.setItem("clearSelections", "true");
		}
		if (onClick) {
			onClick(e);
		}
	};
	if (active) {
		return (
			<Tooltip label={text}>
				<Flex
					id={uniqueIdentifier}
					className={uniqueIdentifier}
					justifySelf="center"
					{...rest}
					bg={"blue.50"}
					as={Link}
					to={link}
					p="0.625em"
					minH="2.5rem"
					minW="2.5rem"
					borderRadius=".375rem"
					onClick={handleClick}
				>
					<Icon size="26px" color="#0087ee" />
				</Flex>
			</Tooltip>
		);
	}
	return (
		<Tooltip label={text}>
			<Flex
				id={uniqueIdentifier}
				className={uniqueIdentifier}
				justifySelf="center"
				{...rest}
				borderRadius=".375rem"
				p="0.625em"
				minH="2.5rem"
				minW="2.5rem"
				as={Link}
				to={link}
				onClick={handleClick}
			>
				<Icon size="26px" />
			</Flex>
		</Tooltip>
	);
};

export const AppShell = ({
	children: Component,
	imageURL,
	pageLoaderURL,
	notifications,
	navigateToDoc,
}: {
	children?: React.LazyExoticComponent<React.FC<any>>;
	imageURL: string;
	pageLoaderURL: string;
	notifications: any[];
	navigateToDoc: Function;
}) => {
	const location = useLocation();
	const pathname = React.useMemo(
		() => location.pathname.split("/"),
		[location.pathname],
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [pageLoadingData, subscriptionData, customerData] = useSelector(
		(state: any) => [
			state.pageLoading,
			state.subscriptionPlan,
			state.customerData,
		],
	);
	const dispatch = useDispatch<AppDispatch>();

	const expectedUserVersion = customerData?.beta_feature_user ? "v2" : "v1";

	const withUi = React.useCallback(
		(to: string) => {
			if (!expectedUserVersion) return to;
			const url = new URL(to, window.location.origin);
			url.searchParams.set("version", expectedUserVersion);
			return url.pathname + url.search + url.hash;
		},
		[expectedUserVersion],
	);

	const [activeTab, setActiveTab] = React.useState<String>("/" + pathname[1]);
	const [isMobileTablet] = useMediaQuery("(max-width: 768px)");
	const [collapsed, setCollapsed] = React.useState(true);
	const [pageHeader, setPageHeader] = React.useState<string>("Dashnode");
	const [isProfileMenuOpen, setIsProfileMenuOpen] =
		React.useState<boolean>(false);
	const [profileMenuPosition, setProfileMenuPosition] = React.useState<any>({
		top: 0,
		left: 0,
	});
	const [showContactSalesPopup, setShowContactSalesPopup] =
		React.useState<boolean>(false);
	const [subscriptionColorTheme, setSubscriptionColorTheme] = React.useState({
		text_color: "#0087EE",
		box_border: "1px dashed #0087EE70",
		box_background_color: "#0087EE10",
		button_background_color: "#0087EE",
		button_hover_color: "#0066b4",
	});
	const [contactMarkdown, setContactMarkdown] = React.useState<string>("");
	// const [showPlans, setShowPlans] = React.useState<boolean>(false);
	// const [showPlanUpgradeConfirmBox, setShowPlanUpgradeConfirmBox] =
	// 	React.useState<boolean>(false);
	// const [selectedPlan, setSelectedPlan] = React.useState<string>("");
	// const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
	const menuRef1 = React.useRef();
	const menuRef2 = React.useRef();
	const MOBILE_SIDEBAR_WIDTH = "60vw";

	const getProfileMenuPosition = (event: any) => {
		const rect = event.currentTarget.getBoundingClientRect();
		setProfileMenuPosition({
			top: rect.top + window.scrollY,
			left: rect.right + window.scrollX,
		});
	};

	const errorHandler = (error: any) => {
		dispatch(
			setPageLoading({
				loading: false,
				loadingText: "",
			}),
		);
		dispatch({
			type: SET_MESSAGE,
			payload: { case: ERROR_MSG, data: formatErrorMessage(error) },
		});
	};

	const toggleCollapsed = () => {
		setCollapsed(!collapsed);
	};

	const extractISO2 = (input: string): string | null => {
		const match = input.match(/\(([^)]+)\)/);
		return match ? match[1] : null;
	};

	const getCustomerData = async () => {
		fetchUserDetails()
			.then((res: any) => {
				const data = res.data.data[0];
				dispatch(setCustomerData(data));
				dispatch(
					setLocalePrefs({
						countryISO2: data?.country ? extractISO2(data?.country) : "IN",
						currency: data?.currency || "INR",
						unitSystem: data?.unit || "Metric",
						lengthUnit: data?.unit
							? data?.unit === "Metric"
								? "mm"
								: "in"
							: "mm",
						massUnit: data?.unit
							? data?.unit === "Metric"
								? "kg"
								: "lb"
							: "kg",
						areaUnit: data?.unit
							? data?.unit === "Metric"
								? "mm2"
								: "in2"
							: "mm2",
					}),
				);
			})
			.catch((error) => {
				errorHandler(error);
			});
	};

	// const changePlan = async () => {
	// 	setButtonLoading(true);
	// 	await frappeMethodService(
	// 		"credit_based_costing.api.pricing_plans.enrole_plan",
	// 		{
	// 			plan_name: selectedPlan,
	// 		},
	// 		"post_with_params"
	// 	)
	// 		.then((response: any) => {
	// 			dispatch({
	// 				type: SET_MESSAGE,
	// 				payload: {
	// 					case: SUCCESS_MSG,
	// 					data: "Upgrade request Submitted!",
	// 				},
	// 			});
	// 			Mixpanel.track("Upgrade Requested", {
	// 				page_name: window.location.pathname.split("/")[1],
	// 				page_url: window.location.href,
	// 				user_id: Cookies.get("user_id"),
	// 				chosen_plan: selectedPlan,
	// 			});
	// 			(window as any).dataLayer?.push({
	// 				event: "Upgrade Requested",
	// 				chosen_plan: selectedPlan,
	// 				user_email: Cookies.get("user_id"),
	// 				user_name: Cookies.get("full_name"),
	// 			});
	// 			refreshSubscriptionPlan(dispatch, false);
	// 		})
	// 		.catch((error: any) => {
	// 			setShowPlans(true);
	// 			errorHandler(error);
	// 		});
	// 	setButtonLoading(false);
	// 	setShowPlanUpgradeConfirmBox(false);
	// };

	useEffect(() => {
		refreshSubscriptionPlan(dispatch, true);
	}, [pathname[1]]);

	useEffect(() => {
		const planName = subscriptionData?.plan || "Free Plan";

		if (String(planName).toLowerCase().includes("pro")) {
			setSubscriptionColorTheme({
				text_color: "#4BA18A",
				box_border: "1px dashed #4BA18A70",
				box_background_color: "#4BA18A10",
				button_background_color: "#4BA18A",
				button_hover_color: "#38846D",
			});
		} else {
			setSubscriptionColorTheme({
				text_color: "#0087EE",
				box_border: "1px dashed #0087EE70",
				box_background_color: "#0087EE10",
				button_background_color: "#0087EE",
				button_hover_color: "#0066b4",
			});
		}
	}, [subscriptionData?.plan]);

	useEffect(() => {
		(window as any).dataLayer?.push({
			event: "VirtualPageView",
			pageUrl: window.location.href,
			pageTitle: window.location.pathname,
			UserEmailId: Cookies.get("user_id"),
			userName: Cookies.get("full_name"),
		});

		Mixpanel.track("VirtualPageView", {
			page_url: window.location.href,
			page_title: window.location.pathname,
			user_email: Cookies.get("user_id"),
			email_id: Cookies.get("user_id"),
			user_name: Cookies.get("full_name"),
		});
	}, [location.pathname, location.search]);

	useEffect(() => {
		setActiveTabBackground();
	}, [location.pathname, location.search]);

	useEffect(() => {
		switch (pathname[1]) {
			case "fileupload":
				setPageHeader("Dashnode");
				break;
			case "itemlibrary":
				if (pathname[2]) setPageHeader("Configure Item");
				else setPageHeader("Item Library");
				break;
			case "projects":
				if (pathname[2]) setPageHeader("Projects");
				else setPageHeader("Projects");
				break;
			case "myprofile":
				setPageHeader("Profile");
				break;
			case "costMaster":
				setPageHeader("Cost Master");
				break;
			case "referral":
				setPageHeader("Referral");
				break;
			case "credits":
				setPageHeader("Credits");
				break;
			default:
				break;
		}
	}, [pathname]);

	useEffect(() => {
		if (isMobileTablet) {
			setCollapsed(true);
		}
	}, [isMobileTablet]);

	useEffect(() => {
		getCustomerData();
	}, []);

	useEffect(() => {
		const authProvider = localStorage.getItem("auth_provider");
		const authAction = localStorage.getItem("auth_action");

		if (authProvider && authAction) {
			const eventPrefix =
				authAction === "login"
					? `${authProvider} Login`
					: `${authProvider} Register`;

			const emailId = Cookies.get("user_id");

			(window as any).dataLayer?.push({
				event: `${eventPrefix} Success`,
				email_id: emailId,
				provider: authProvider,
				success: true,
			});

			Mixpanel.track(`${eventPrefix} Success`, {
				email_id: emailId,
				provider: authProvider,
				success: true,
			});

			localStorage.removeItem("auth_provider");
			localStorage.removeItem("auth_action");
		}
	}, []);

	useEffect(() => {
		if (!customerData) return;

		const expectedVersion = customerData?.beta_feature_user ? "v2" : "v1";
		const urlVersion = searchParams.get("version");

		if (urlVersion !== expectedVersion) {
			const next = new URLSearchParams(searchParams);
			next.set("version", expectedVersion);
			setSearchParams(next, { replace: true });
		}
	}, [customerData, location.search]);

	const setActiveTabBackground = () => {
		switch (pathname[1]) {
			case "fileupload":
				setActiveTab("/fileupload");
				document.title = "Home";
				break;
			case "itemlibrary":
				setActiveTab("/itemlibrary");
				document.title = "Item Library";
				break;
			case "projects":
				setActiveTab("/projects?direct=false");
				document.title = "Projects";
				break;
			case "costMaster":
				setActiveTab("/costMaster");
				document.title = "Cost Master";
				break;
			case "myprofile":
				setActiveTab(`/myprofile`);
				if (pathname[2] && pathname[2] === "referral")
					document.title = "Referral";
				else if (searchParams?.get("tab") === "materialRate")
					document.title = "Material Rates";
				else if (searchParams?.get("tab") === "machiningRate")
					document.title = "Machining Rates";
				else document.title = "My Profile";
				break;
			case "credits":
				setActiveTab("/credits");
				document.title = "Credits";
				break;
			default:
				break;
		}
	};

	const menuItems = [
		{
			icon: BsCloudUploadFill,
			text: "File upload",
			sideBarText: "File Upload",
			link: withUi("/fileupload"),
			uniqueIdentifier: "dashboard-navigator",
		},
		{
			icon: BsGearWideConnected,
			text: "Item Library",
			sideBarText: "Item Library",
			link: withUi("/itemlibrary"),
			uniqueIdentifier: "item-library-navigator",
		},
		{
			icon: BsFolderFill,
			text: "Projects",
			sideBarText: "Projects",
			link: withUi("/projects?direct=false"),
			hidden: customerData?.beta_feature_user,
			uniqueIdentifier: "projects-navigator",
		},
		{
			icon: BsLayersHalf,
			text: "Cost Master",
			sideBarText: "Cost Master",
			link: withUi("/costMaster"),
			uniqueIdentifier: "cost-master-navigator",
		},
		{
			icon: BsPersonFill,
			sideBarText: "My Profile",
			text: "My Profile",
			link: withUi("/myprofile"),
			uniqueIdentifier: "my-profile-navigator",
		},
	];

	const handleSignout = () => {
		(window as any).dataLayer?.push({
			event: "User Signout",
			page_url: window.location.href,
			page_exit_url: window.location.href,
			page_exit_name: window.location.pathname,
			user_id: Cookies.get("user_id"),
		});
		Mixpanel.track("signOut", {
			page_url: window.location.href,
			page_exit_url: window.location.href,
			page_exit_name: window.location.pathname,
			user_id: Cookies.get("user_id"),
			email_id: Cookies.get("user_id"),
		});
		//logout here
		dispatch(logoutAce());
	};

	const variants = {
		open: { opacity: 1, x: "0" },
		closed: { opacity: 1, x: "0" },
	};

	const sideVariants = {
		closed: {
			transition: {
				staggerChildren: 0.2,
				staggerDirection: -1,
			},
		},
		open: {
			transition: {
				staggerChildren: 0.2,
				staggerDirection: 1,
			},
		},
	};

	const triggerContactSalesEmail = () => {
		const subject = encodeURIComponent("Upgrade to Paid Plans");
		const body = encodeURIComponent(
			"Hi Team, we are interested in upgrading to paid plans. Can you help me with the pricing?",
		);

		const mailtoUrl = `mailto:team@dashnode.ai?subject=${subject}&body=${body}`;

		(window as any).dataLayer?.push({
			event: "Upgrade plan Contact sales Email",
			user_email: Cookies.get("user_id"),
			full_name: Cookies.get("full_name"),
			current_plan: subscriptionData?.plan,
		});
		Mixpanel.track("Upgrade plan Contact sales Email", {
			user_email: Cookies.get("user_id"),
			full_name: Cookies.get("full_name"),
			current_plan: subscriptionData?.plan,
			email_id: Cookies.get("user_id"),
		});

		window.location.href = mailtoUrl;
	};

	const getPathname = (to?: string) => {
		if (!to) return "";
		try {
			return new URL(to, window.location.origin).pathname;
		} catch {
			return String(to).split("?")[0];
		}
	};

	const normalizePath = (p: string) => {
		// remove trailing slashes, but keep root "/"
		if (!p) return "";
		return p.length > 1 ? p.replace(/\/+$/, "") : p;
	};

	const isItemActive = (to?: string) => {
		const itemPathRaw = getPathname(to);
		const itemPath = normalizePath(itemPathRaw);
		const currentPath = normalizePath(location.pathname);

		if (!itemPath) return false;
		if (itemPath === "/") return currentPath === "/";

		// Exact match OR "current is a child route of item"
		return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
	};

	const creditStatus = () => {
		return (
			<Flex
				id="credits-navigator"
				className="credits-navigator"
				alignItems="center"
				justifyContent="space-between"
				flexWrap="wrap"
				_hover={{
					color: "#0087EE",
				}}
				bg={pathname[1] === "credits" ? "#0087EE1A" : "#transparent"}
				p=".625rem"
				borderRadius=".5rem"
				width="100%"
				gap=".5rem"
				cursor="pointer"
				onClick={() => {
					(window as any).dataLayer?.push({
						event: "Sidebar Credits Button Click",
						user_email: Cookies.get("user_id"),
						full_name: Cookies.get("full_name"),
					});
					Mixpanel.track("Sidebar Credits Button Click", {
						user_email: Cookies.get("user_id"),
						full_name: Cookies.get("full_name"),
						email_id: Cookies.get("user_id"),
					});
					navigate(withUi("/credits"));
				}}
				role="group"
				borderLeft={
					collapsed && activeTab === "/credits" ? "4px solid #0087ee" : ""
				}
			>
				<Text
					display="flex"
					flexDirection={collapsed ? "column" : "row"}
					alignItems="center"
					justifyContent={collapsed ? "center" : "flex-start"}
					width={collapsed ? "100%" : "auto"}
					gap={collapsed ? ".3rem" : ".6875rem"}
					color={activeTab === "/credits" ? "#0087EE" : "#000"}
					fontSize={
						collapsed ? (activeTab === "/credits" ? "13px" : "11px") : "1rem"
					}
					fontWeight={activeTab === "/credits" ? 500 : 400}
					textAlign="center"
					_groupHover={{ color: "#0087EE" }}
				>
					<IoWallet size={collapsed ? "1.625rem" : "1.25rem"} />
				</Text>
				<Flex
					alignItems="center"
					justifyContent="center"
					flexWrap="wrap"
					bg={pathname[1] === "credits" ? "#fff" : "#0087EE1A"}
					borderRadius={collapsed ? ".75rem" : "50px"}
					p={collapsed ? ".125rem" : ".25rem"}
					minW={collapsed ? "100%" : "5.875rem"}
					minH="2rem"
					objectFit="contain"
					fontSize={collapsed ? "13px" : ".875rem"}
					fontWeight={collapsed ? 400 : 500}
					color="#0087EE"
					textAlign="center"
					wordBreak="break-word"
					gap=".125rem"
				>
					<Text>{subscriptionData?.credits ?? 0}</Text>
					<Text>Credits</Text>
				</Flex>
			</Flex>
		);
	};

	const subscriptionStatus = () => {
		return (
			<Flex
				id="subscription-details-area"
				className="subscription-details-area"
				flexDirection="column"
				justifyContent="center"
				border={subscriptionColorTheme?.box_border}
				bg={subscriptionColorTheme?.box_background_color}
				width={collapsed ? "87.5%" : "98%"}
				borderRadius=".5rem"
				p="1rem .375rem"
				mb=".75rem"
				textAlign={collapsed ? "center" : "left"}
			>
				<Text
					fontSize="14px"
					fontWeight="semibold"
					color={subscriptionColorTheme?.text_color}
					wordBreak="break-word"
				>
					{subscriptionData?.plan}
				</Text>
				<Flex
					alignItems="center"
					justifyContent={collapsed ? "center" : "flex-start"}
					flexWrap="wrap"
					gap="0rem 0.2rem"
				>
					<Text fontSize=".75rem" color="#00000080">
						Expires on
					</Text>
					<Text
						fontSize=".75rem"
						color="#00000080"
						wordBreak="keep-all"
						whiteSpace="nowrap"
						overflowWrap="normal"
					>
						{subscriptionData?.plan_expiry_date}
					</Text>
				</Flex>
				<Flex
					alignItems="center"
					justifyContent="space-between"
					flexWrap="wrap"
					mt=".75rem"
					gap=".25rem"
				>
					<Box w={collapsed ? "100%" : "47%"}>
						<Text fontSize={collapsed ? ".75rem" : ".875rem"}>
							File uploads usage
						</Text>
					</Box>
					<Text
						fontSize=".875rem"
						color={subscriptionColorTheme?.text_color}
						bg="#fff"
						borderRadius="5rem"
						p=".25rem"
						w={collapsed ? "100%" : "47%"}
						textAlign="center"
					>{`${subscriptionData?.used_file_uploads}${
						collapsed ? " / " : " out of "
					}${subscriptionData?.allocated_file_uploads}`}</Text>
				</Flex>
				<Text
					display={collapsed ? "none" : ""}
					color="#00000070"
					fontSize=".625rem"
					mt=".5rem"
				>
					This plan will be renewed next month
				</Text>
				<Button
					className="aceContactSalesBtn"
					id="ace_contact_sales_btn"
					children="Contact Sales"
					sx={{
						whiteSpace: "normal",
						overflowWrap: "break-word",
					}}
					color="#fff"
					bg={subscriptionColorTheme?.text_color}
					_hover={{ bg: subscriptionColorTheme?.button_hover_color }}
					size="md"
					fontSize={collapsed ? "sm" : "md"}
					width="100%"
					mt=".875rem"
					onClick={() => {
						fetchPublicDisplaySettings()
							.then((d) => {
								setContactMarkdown(d.contact_information || "");
								setShowContactSalesPopup(true);
							})
							.catch(() => setContactMarkdown(""));
					}}
				/>
				{/* <Button
					className="aceShowSubscriptionPlansBtn"
					id="ace_show_subscription_plans_btn"
					children={
						subscriptionData.plan?.toLowerCase().includes("pro")
							? "Change Plan"
							: "Upgrade Plan"
					}
					sx={{
						whiteSpace: "normal",
						overflowWrap: "break-word",
					}}
					color="#fff"
					bg={subscriptionColorTheme?.text_color}
					_hover={{ bg: subscriptionColorTheme?.button_hover_color }}
					size="md"
					fontSize={collapsed ? "sm" : "md"}
					width="100%"
					mt=".875rem"
					onClick={() => setShowPlans(true)}
				/>
				<Text
					color={subscriptionColorTheme?.text_color}
					textAlign="center"
					fontSize=".75rem"
					mt={collapsed ? ".5rem" : ".625rem"}
					cursor="pointer"
					onClick={() => setShowPlans(true)}
				>
					View All Plans
				</Text>  */}
			</Flex>
		);
	};

	type PublicDisplaySettings = {
		contact_information?: string;
	};

	async function fetchPublicDisplaySettings(): Promise<PublicDisplaySettings> {
		const res = await fetch(
			'/api/resource/Public Display Settings/Public Display Settings?fields=["contact_information"]',
		);

		if (!res.ok) throw new Error("Failed to load public display settings");

		const json = await res.json();
		return json.data as PublicDisplaySettings;
	}

	return (
		<Flex bg="#f3f6f9" height="100dvh" overflow="hidden">
			{/* backdrop for mobile sidebar */}
			{isMobileTablet && !collapsed && (
				<Box
					position="absolute"
					inset={0}
					bg="blackAlpha.300"
					zIndex={1000}
					onClick={() => setCollapsed(true)} // tap backdrop to close
				/>
			)}
			{!(collapsed && isMobileTablet) && (
				<Box
					w={
						collapsed
							? isMobileTablet
								? "0%"
								: "7%"
							: isMobileTablet
								? MOBILE_SIDEBAR_WIDTH
								: "20%"
					}
					as={motion.div}
					display="flex"
					flexShrink="0"
					zIndex={1001}
					h="100dvh"
					// position={isMobileTablet ? "absolute" : "relative"}
					position={isMobileTablet ? "fixed" : "relative"}
					flexDirection="column"
					justifyContent="space-between"
					animate={{ x: "0" }}
					transition={{ type: "inertia" }}
					variants={variants}
					initial={{ x: "-100%" }}
					exit={{ x: "-100%" }}
					bg="#fff"
					overflowY="auto"
					overflowX="hidden"
					borderRight={isMobileTablet ? ".1px solid #f3f6f9" : ""}
					boxShadow={isMobileTablet && !collapsed ? "lg" : "none"}
					borderRightWidth={isMobileTablet ? 0 : "1px"}
					sx={{
						overscrollBehaviorY: "contain", // stop scroll chaining / over-scrolling
						scrollbarGutter: "stable", // avoid layout shift when scrollbar appears
						WebkitOverflowScrolling: "auto", // disable iOS momentum bounce
					}}
				>
					<motion.div
						animate={collapsed ? "closed" : "open"}
						variants={variants}
					>
						<Flex
							flexDirection="column"
							w="100%"
							justifyContent="space-between"
						>
							{collapsed ? (
								<Flex
									flexDirection="column"
									width="100%"
									alignItems="center"
									justifyContent="space-between"
									height="100vh"
								>
									<Flex flexDirection="column" width="100%" alignItems="center">
										<Image
											objectFit={"contain"}
											w="50%"
											src={DashnodeLogo}
											m=".875rem 0 1.25rem 0"
											cursor="pointer"
											onClick={() => {
												navigate(withUi("/fileupload"));
												if (isMobileTablet) setCollapsed(true);
											}}
										/>

										{menuItems
											.filter((each: any) => !each.hidden)
											?.map((item, index) => {
												return (
													<motion.div
														key={index}
														style={{
															width: "100%",
														}}
													>
														<IconMini
															key={item.text}
															mt=".75em"
															mx="auto"
															Icon={item.icon}
															alignItems="center"
															active={isItemActive(item.link)}
															_hover={{ color: "#0087ee" }}
															ref={
																item?.text === "My Profile" ? menuRef1 : null
															}
															link={
																item.text === "My Profile"
																	? undefined
																	: item.link
															}
															onClick={(e) => {
																if (item.text === "My Profile") {
																	e.preventDefault();
																	getProfileMenuPosition(e);
																	setIsProfileMenuOpen(true);
																} else {
																	setActiveTab(item.link);
																	localStorage.removeItem("uploadedFiles");
																	localStorage.removeItem("selected_items");
																	localStorage.setItem(
																		"clearSelections",
																		"true",
																	);
																	if (item.text === "Cost Master") {
																		(window as any).dataLayer?.push({
																			event: `Cost Master`,
																			user_id: Cookies.get("user_id"),
																			page_name: "Sidebar",
																		});
																		Mixpanel.track(`Cost Master`, {
																			user_email: Cookies.get("user_id"),
																			page_name: "Sidebar",
																			email_id: Cookies.get("user_id"),
																		});
																	}
																}
															}}
															text={item.sideBarText}
															uniqueIdentifier={item?.uniqueIdentifier}
														/>
													</motion.div>
												);
											})}
									</Flex>
									<Flex
										alignItems="center"
										flexDirection="column"
										justifyContent="center"
										gap=".5rem"
									>
										{creditStatus()}
										{subscriptionStatus()}
									</Flex>
								</Flex>
							) : (
								<Box padding="0 0.5rem">
									<Flex
										flexDirection="column"
										width="100%"
										justifyContent={
											isMobileTablet ? "flex-start" : "space-between"
										}
										height="100vh"
									>
										<Box>
											<Image
												w="60%"
												src={imageURL}
												m=".8rem 0 2rem .5rem"
												cursor="pointer"
												onClick={() => {
													navigate(withUi("/fileupload"));
													if (isMobileTablet) setCollapsed(true);
												}}
											/>
											{isMobileTablet && (
												<Flex
													mb="1.5rem"
													ml="1rem"
													justifyContent="flex-start"
													alignItems="center"
													gap="1.25rem"
												>
													<Box
														borderRadius="50%"
														bg="rgba(0, 135, 238, 0.1)"
														color="rgba(0, 135, 238, 1)"
														width="3.5rem"
														height="3.5rem"
														display="flex"
														alignItems="center"
														justifyContent="center"
														cursor="pointer"
														onClick={() => navigate(withUi("/myprofile"))}
													>
														<Text fontSize="2.5rem" fontWeight="600">
															{Cookies.get("full_name")
																?.charAt(0)
																.toUpperCase()}
														</Text>
													</Box>
													<Flex flexDir="column" gap=".375rem">
														<Box
															cursor="pointer"
															onClick={() => navigate(withUi("/myprofile"))}
														>
															<Text
																textStyle="primary.contentTitle"
																fontWeight="600"
																wordBreak="break-word"
																lineHeight="1rem"
															>
																{Cookies.get("full_name")}
															</Text>
															<Text
																fontSize=".75rem"
																wordBreak="break-word"
																color="#00000090"
																lineHeight=".875rem"
															>
																{Cookies.get("user_id")}
															</Text>
														</Box>
														<Text
															textColor="rgba(0, 135, 238, 1)"
															onClick={handleSignout}
															cursor="pointer"
															fontSize=".875rem"
															fontWeight="semibold"
														>
															Sign Out
														</Text>
													</Flex>
												</Flex>
											)}
											{menuItems
												.filter((each: any) => !each.hidden)
												?.map((item, index) => (
													<motion.div key={index} variants={sideVariants}>
														<Box
															position={
																item.text === "My Profile"
																	? "relative"
																	: undefined
															}
															p="0"
															m="0"
														>
															<Flex align="center">
																<IconText
																	key={item.text}
																	Icon={item.icon}
																	text={item.text}
																	fontSize="14px"
																	p="1rem 0.5rem"
																	mb="1rem"
																	active={isItemActive(item.link)}
																	_hover={{ color: "#0087ee" }}
																	ref={
																		item.text === "My Profile" ? menuRef2 : null
																	}
																	link={
																		item.text === "My Profile"
																			? undefined
																			: item.link
																	}
																	onClick={(e) => {
																		if (item.text === "My Profile") {
																			e.preventDefault(); // don't follow the "#" link
																			getProfileMenuPosition(e);
																			setIsProfileMenuOpen(true);
																			return; // stop here – do not collapse
																		}
																		// normal nav for other items
																		setActiveTab(item.link);
																		localStorage.removeItem("uploadedFiles");
																		localStorage.removeItem("selected_items");
																		localStorage.setItem(
																			"clearSelections",
																			"true",
																		);
																		if (isMobileTablet) setCollapsed(true);
																	}}
																	uniqueIdentifier={item?.uniqueIdentifier}
																/>
															</Flex>
														</Box>
													</motion.div>
												))}
										</Box>

										<Flex
											flexDir="column"
											alignItems="center"
											justifyContent="center"
										>
											{creditStatus()}
											{subscriptionStatus()}
										</Flex>

										{/* {isMobileTablet && (
											<Flex justifyContent="flex-end">
												<MdKeyboardDoubleArrowLeft
													size="2rem"
													color="#0087EE"
													cursor="pointer"
													onClick={() => setCollapsed(true)}
												/>
											</Flex>
										)} */}
									</Flex>
								</Box>
							)}
						</Flex>
					</motion.div>
				</Box>
			)}
			<Flex
				direction="column"
				w={
					collapsed
						? isMobileTablet
							? "100%"
							: "93%"
						: isMobileTablet
							? "100%"
							: "80%"
				}
			>
				<Box
					h="65px"
					bg="white"
					marginBottom="2px"
					flexShrink={0}
					borderLeft="1px solid #f3f6f9"
					display={isMobileTablet ? "block" : "none"}
				>
					<Flex
						h="100%"
						alignItems="center"
						justifyContent="space-between"
						padding="10px"
						zIndex={999}
					>
						<Flex alignItems="center" fontWeight="600">
							{!isMobileTablet ? (
								<motion.div
									initial={{ scale: 1 }}
									animate={{ rotateY: !collapsed ? 180 : 0 }}
									transition={{ duration: 0.5 }}
								>
									<IconButton
										icon={<MdKeyboardDoubleArrowRight size={"100%"} />}
										boxSize={6}
										background={"none"}
										_hover={{ color: "#0087ee" }}
										_active={{ backgroundColor: "transparent" }}
										aria-label={
											collapsed ? "Expand sidebar" : "Collapse sidebar"
										}
										onClick={toggleCollapsed}
									/>
								</motion.div>
							) : (
								<motion.div
									initial={{ scale: 1 }}
									animate={{ rotateY: !collapsed ? 180 : 0 }}
									transition={{ duration: 0.5 }}
									style={{ marginRight: "0.8rem" }}
								>
									{collapsed && (
										<AiOutlineMenu
											size="1.5rem"
											onClick={toggleCollapsed}
											cursor="pointer"
											style={{ color: "#0087ee" }}
										/>
									)}
								</motion.div>
							)}
							<Flex alignItems="center" justifyContent="center" gap=".25rem">
								{isMobileTablet && pageHeader === "Configure Item" && (
									<MdArrowBackIosNew
										size="1.25rem"
										cursor="pointer"
										onClick={() => navigate(-1)}
									/>
								)}
								{pageHeader}
							</Flex>
						</Flex>
						{/* --------------current subscription plan-------------- */}
						<Flex
							alignItems="center"
							gap=".625rem"
							p=".375rem .625rem"
							bg={subscriptionColorTheme?.box_background_color}
							borderRadius="150px"
						>
							<Image
								src={
									subscriptionData.plan?.toLowerCase().includes("pro")
										? ShieldZapGreen
										: checkVerifiedBlue
								}
							/>
							<Text
								fontSize=".75rem"
								fontWeight="semibold"
								lineHeight="15px"
								color={subscriptionColorTheme?.text_color}
							>
								{subscriptionData?.plan}
							</Text>
						</Flex>
						{/* --------------current subscription plan-------------- */}
					</Flex>
				</Box>
				<Flex bg="#f3f6f9" flexGrow="1" overflow="auto">
					{!!Component && <Component />}
				</Flex>
			</Flex>
			<Menu
				isOpen={isProfileMenuOpen}
				placement="right"
				onClose={() => setIsProfileMenuOpen(false)}
			>
				<MenuList
					position="absolute"
					top={profileMenuPosition?.top}
					left={profileMenuPosition?.left}
					zIndex="9999"
					px=".25rem"
				>
					<MenuItem
						as={Link}
						to={withUi("/myprofile")}
						_hover={{
							color: "black",
							fontWeight: "500",
							backgroundColor: "gray.100",
						}}
						borderRadius=".5rem"
						fontWeight={!pathname[2] && pathname[1] === "myprofile" ? 500 : 400}
						backgroundColor={
							!pathname[2] && pathname[1] === "myprofile" ? "#0087EE1A" : ""
						}
						color={
							!pathname[2] && pathname[1] === "myprofile"
								? "#0087EE"
								: "#00000080"
						}
					>
						My Profile
					</MenuItem>
					<MenuItem
						as={Link}
						to={withUi("/myprofile/referral")}
						_hover={{
							color: "black",
							fontWeight: "500",
							backgroundColor: "gray.100",
						}}
						borderRadius=".5rem"
						fontWeight={pathname[2] && pathname[2] === "referral" ? 500 : 400}
						backgroundColor={
							pathname[2] && pathname[2] === "referral" ? "#0087EE1A" : ""
						}
						color={
							pathname[2] && pathname[2] === "referral"
								? "#0087EE"
								: "#00000080"
						}
					>
						Referral
					</MenuItem>
					<MenuItem
						as="button"
						onClick={handleSignout}
						color="#00000080"
						_hover={{
							color: "black",
							fontWeight: "500",
							backgroundColor: "gray.100",
						}}
						borderRadius=".5rem"
					>
						<Box>
							<Text>Sign Out</Text>
							<Flex alignItems="center" gap=".125rem" color="#00000080">
								<Text fontSize=".75rem" fontWeight="500">
									{`${Cookies.get("full_name")?.split(" ")[0]}`}
								</Text>
								<Text fontSize="11px" fontWeight="400">
									({Cookies.get("user_id")})
								</Text>
							</Flex>
						</Box>
					</MenuItem>
				</MenuList>
			</Menu>

			<BasicDialog
				isOpen={showContactSalesPopup}
				onClose={() => setShowContactSalesPopup(false)}
				header="Contact Sales"
				showHeaderBottomBorder
				content={
					<Flex flexDir="column" p="1rem 1.5rem">
						<MarkdownPreset content={contactMarkdown || "_Loading..._"} />
						<Button
							children="Email Us"
							mt="1rem"
							color="#fff"
							bg="#0087EE"
							_hover={{ bg: "#0066B4" }}
							leftIcon={<FiMail />}
							onClick={() => triggerContactSalesEmail()}
						/>
					</Flex>
				}
			/>

			{pageLoadingData?.loading && (
				<PageLoading
					pageLoading={pageLoadingData}
					fileUploadPageLoader={FileUploadPageLoader}
					pageLoaderURL={pageLoaderURL}
				/>
			)}

			{/* Global Upload Progress Indicator */}
			<GlobalUploadProgress />
			
			{/* Missing Info Prompt */}
			<MissingInfoPopup />
		</Flex>
	);
};
